import { request } from "$/lib/api"
import { Route, VatRate, populateRoute } from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

export interface CustomBookingSection {
  id: string
  name: string
  storeId: string
  items: CustomBookingSectionItem[]
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null
}

interface CustomBookingSectionItem {
  id: string
  name: string
  description: string | null
  price: number
  image: string | null
  displayOrder: number
  category: string | null
  vatRate: VatRate | null
  sectionId: string
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null
}

interface CreateCustomBookingSection {
  name: string
}

interface UpdateCustomBookingSection {
  name?: string
}

interface CreateCustomBookingSectionItem {
  name: string
  description?: string | null
  price: number
  image?: File | null
  displayOrder?: number
  category?: string | null
  vatRate?: VatRate | null
}

interface UpdateCustomBookingSectionItem {
  name?: string
  description?: string | null
  price?: number
  image?: File | null
  displayOrder?: number
  category?: string | null
  vatRate?: VatRate | null
}

interface SectionFilters {
  storeId: string
}

async function fetchSections(
  filters: SectionFilters,
): Promise<CustomBookingSection[]> {
  const { storeId } = filters

  return await request<CustomBookingSection[]>({
    url: populateRoute(
      Route.CUSTOM_BOOKING_SECTION_CONTROLLER__FIND_MANY_BY_STORE_ID,
      storeId,
    ),
    method: "GET",
  })
}

async function createSection(
  data: CreateCustomBookingSection,
  storeId: string,
) {
  return await request<CustomBookingSection>({
    url: populateRoute(
      Route.CUSTOM_BOOKING_SECTION_CONTROLLER__CREATE,
      storeId,
    ),
    method: "POST",
    data,
  })
}

async function updateSection(
  data: UpdateCustomBookingSection,
  sectionId: string,
) {
  return await request<CustomBookingSection>({
    url: populateRoute(
      Route.CUSTOM_BOOKING_SECTION_CONTROLLER__UPDATE,
      sectionId,
    ),
    method: "PATCH",
    data,
  })
}

async function deleteSection(sectionId: string) {
  return await request<void>({
    url: populateRoute(
      Route.CUSTOM_BOOKING_SECTION_CONTROLLER__DELETE,
      sectionId,
    ),
    method: "DELETE",
  })
}

async function createItem(
  data: CreateCustomBookingSectionItem,
  sectionId: string,
) {
  const formData = new FormData()
  formData.append("name", data.name)
  if (data.description !== undefined && data.description !== null) {
    formData.append("description", data.description)
  }
  formData.append("price", data.price.toString())
  if (data.displayOrder !== undefined) {
    formData.append("displayOrder", data.displayOrder.toString())
  }
  formData.append("category", data.category ?? "")
  // An empty value means "Automatisk" (follow the normal VAT rules).
  formData.append("vatRate", data.vatRate?.toString() ?? "")
  if (data.image) {
    formData.append("image", data.image)
  }

  return await request<CustomBookingSectionItem>({
    url: populateRoute(
      Route.CUSTOM_BOOKING_SECTION_CONTROLLER__CREATE_ITEM,
      sectionId,
    ),
    method: "POST",
    data: formData,
  })
}

async function updateItem(
  data: UpdateCustomBookingSectionItem,
  itemId: string,
) {
  const formData = new FormData()
  if (data.name !== undefined) {
    formData.append("name", data.name)
  }
  if (data.description !== undefined && data.description !== null) {
    formData.append("description", data.description)
  }
  if (data.price !== undefined) {
    formData.append("price", data.price.toString())
  }
  if (data.displayOrder !== undefined) {
    formData.append("displayOrder", data.displayOrder.toString())
  }
  if (data.category !== undefined) {
    formData.append("category", data.category ?? "")
  }
  if (data.vatRate !== undefined) {
    formData.append("vatRate", data.vatRate?.toString() ?? "")
  }
  if (data.image) {
    formData.append("image", data.image)
  }

  return await request<CustomBookingSectionItem>({
    url: populateRoute(
      Route.CUSTOM_BOOKING_SECTION_CONTROLLER__UPDATE_ITEM,
      itemId,
    ),
    method: "PATCH",
    data: formData,
  })
}

async function deleteItem(itemId: string) {
  return await request<void>({
    url: populateRoute(
      Route.CUSTOM_BOOKING_SECTION_CONTROLLER__DELETE_ITEM,
      itemId,
    ),
    method: "DELETE",
  })
}

export const useCustomBookingSectionMutations = (storeId: string) => {
  const queryClient = useQueryClient()

  const invalidateQueries = async () =>
    await queryClient.invalidateQueries(
      customBookingSectionQueries.list({ storeId }),
    )

  const createSectionMutation = useMutation({
    mutationFn: (data: CreateCustomBookingSection) =>
      createSection(data, storeId),
    onSettled: invalidateQueries,
  })

  const updateSectionMutation = useMutation({
    mutationFn: ({
      data,
      id,
    }: { data: UpdateCustomBookingSection; id: string }) =>
      updateSection(data, id),
    onSettled: invalidateQueries,
  })

  const deleteSectionMutation = useMutation({
    mutationFn: (ids: string[]) => {
      const promises = ids.map((id) => deleteSection(id))
      return Promise.all(promises)
    },
    onSettled: invalidateQueries,
  })

  const createItemMutation = useMutation({
    mutationFn: ({
      data,
      sectionId,
    }: { data: CreateCustomBookingSectionItem; sectionId: string }) =>
      createItem(data, sectionId),
    onSettled: invalidateQueries,
  })

  const updateItemMutation = useMutation({
    mutationFn: ({
      data,
      id,
    }: { data: UpdateCustomBookingSectionItem; id: string }) =>
      updateItem(data, id),
    onSettled: invalidateQueries,
  })

  const deleteItemMutation = useMutation({
    mutationFn: (ids: string[]) => {
      const promises = ids.map((id) => deleteItem(id))
      return Promise.all(promises)
    },
    onSettled: invalidateQueries,
  })

  return {
    createSectionMutation,
    updateSectionMutation,
    deleteSectionMutation,
    createItemMutation,
    updateItemMutation,
    deleteItemMutation,
  }
}

const customBookingSectionQueries = {
  all: () => ["custom-booking-section"],
  lists: () => [...customBookingSectionQueries.all(), "list"],
  list: (filters: SectionFilters) =>
    queryOptions({
      queryKey: [...customBookingSectionQueries.lists(), filters],
      queryFn: () => fetchSections(filters),
    }),
}

export default customBookingSectionQueries
